import "twin.macro";

import { ChevronRight, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useClients } from "@/api/clients/queries";
import { Client } from "@/api/clients/types";
import { Button } from "@/components/buttons";
import { LoadingSpinner } from "@/components/LoadingIndicator";
import { PageContainer, PageTitle } from "@/components/page";
import SearchBar from "@/components/SearchBar";
import { ClientForm } from "@/routes/clients/CreateClientFormModale";

const Clients = () => {
  const { t } = useTranslation();
  const { data: clients, isLoading } = useClients();
  const [search, setSearch] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [filterClients, setFilterClients] = useState<Client[]>(clients || []);

  const handleOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (clients) {
      setFilterClients(
        clients.filter((client) =>
          client.name.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search]);

  useEffect(() => {
    if (clients) {
      setFilterClients(clients);
    }
  }, [clients]);

  return (
    <PageContainer>
      <div tw="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div tw="sm:flex sm:items-center">
          <div tw="sm:flex-auto">
            <PageTitle>{t("clients.title")}</PageTitle>
          </div>
          <div tw="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <Button secondary onClick={handleOpen} tw="shadow">
              <PlusIcon tw="w-5 h-5 mr-2" />
              {t("clients.new")}
            </Button>
          </div>
        </div>
        <div tw="mt-8">
          <SearchBar search={search} setSearch={setSearch} />
        </div>
        <div tw="mt-8 flow-root">
          <div tw="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div tw="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div tw="overflow-hidden shadow ring-1 ring-black/5 sm:rounded-lg">
                {isLoading ? (
                  <div tw="flex items-center justify-center h-96">
                    <LoadingSpinner tw="w-12 h-12 text-primary-800" />
                  </div>
                ) : !isLoading && clients && clients.length === 0 ? (
                  <div tw="flex items-center justify-center h-96">
                    <p tw="text-lg text-gray-500">{t("clients.noClient")}</p>
                  </div>
                ) : (
                  <table tw="min-w-full divide-y divide-gray-300">
                    <thead tw="bg-primary-800 font-bold">
                      <tr>
                        {/* <th tw="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-white sm:pl-6">
                          <span tw="sr-only">Open</span>
                        </th> */}
                        {/* <th
                          scope="col"
                          tw="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-white sm:pl-6"
                        >
                          Type
                        </th> */}
                        <th
                          scope="col"
                          tw="pl-8 py-3.5 text-left text-sm font-semibold col-span-2 text-white"
                        >
                          {t("clients.name")}
                        </th>
                        {/* <th
                          scope="col"
                          tw="px-3 py-3.5 text-left text-sm font-semibold text-white"
                        >
                          {t("clients.email")}
                        </th> */}
                        <th
                          scope="col"
                          tw="px-3 py-3.5 text-left text-sm font-semibold text-white"
                        >
                          {t("clients.city")}
                        </th>
                        <th scope="col" tw="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span tw="sr-only">Edit</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody tw="divide-y divide-gray-200 bg-white">
                      {clients &&
                        clients.length > 0 &&
                        filterClients.map((client) => (
                          <ClientComponent key={client._id} client={client} />
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>

        <ClientForm open={open} setOpen={setOpen} />
      </div>
    </PageContainer>
  );
};

const ClientComponent = ({ client }: { client: Client }) => {
  const navigate = useNavigate();
  // const [openUserList, setOpenUserList] = useState<boolean>(false);
  // const handleOpenUserList = () => {
  //   setOpenUserList(!openUserList);
  // };

  const goToClientPage = () => {
    navigate(`/clients/${client._id}/informations`);
  };

  return (
    <Fragment>
      <tr
        key={client._id}
        tw="bg-white cursor-pointer"
        onClick={goToClientPage}
      >
        {/* <td tw="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
          {client.users && client.users.length > 0 && (
            <ChevronDownIcon
              tw="w-5 group-data-[open]:rotate-180 cursor-pointer"
              onClick={() => handleOpenUserList()}
            />
          )}
        </td> */}
        {/* <td tw="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
          {TypeOptionsBadge(client.type)}
        </td> */}
        <td tw="whitespace-nowrap pl-8 py-4 text-sm text-gray-600">
          {client.name}
        </td>
        {/* <td tw="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
          {client.email}
        </td> */}
        <td tw="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
          {client.city}
        </td>
        <td tw="relative whitespace-nowrap px-3 py-4 text-sm font-medium sm:pr-6">
          <a tw="flex flex-1 justify-end text-gray-600 hover:text-gray-900 px-3 py-4 ">
            <ChevronRight tw="w-5 h-5" />
          </a>
        </td>
      </tr>
      {/* {client.users &&
        client.users.length > 0 &&
        openUserList &&
        client.users.map((user, idx) => (
          <Fragment key={idx}>
            <UserComponent user={user} />
          </Fragment>
        ))} */}
    </Fragment>
  );
};

// const UserComponent = ({ user }: { user: User }) => {
//   return (
//     <tr tw="bg-secondary-50">
//       <td tw="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 sr-only"></td>
//       <td tw="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"></td>
//       <td tw="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
//         {user.lastname} {user.firstname}
//       </td>
//       <td tw="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
//         {user.email}
//       </td>
//       <td tw="px-3 py-4 text-sm text-gray-600"></td>
//       <td tw="relative py-4 pl-3 pr-4  text-sm font-medium sm:pr-6"></td>
//     </tr>
//   );
// };

export default Clients;
